import { useCallback, useEffect, useRef, useState } from 'react';
import { patchMarketingStatusRow } from 'components/TableCells/MarketingStatusCell/patchMarketingStatusRow';
import { publish } from 'lib/EventBuser';
import { REFRESH_TABLE } from 'lib/events';
import { pollBulkUntilComplete } from 'services/BulkService';
import { getLiteral } from 'utils/getLiteral';
import { successToast, errorToast } from 'utils/toast';
import useGiveConsent from './useGiveConsent';

export const BULK_MARKETING_SUBSCRIBE_CONFIRM_CLOSE_TIMEOUT = 250;

function showMarketingStatusUpdateError() {
    errorToast({
        title: getLiteral('title_toast_marketing_campaigns_marketing_status_update_error'),
        text: getLiteral('label_toast_marketing_campaigns_marketing_status_update_error_message'),
    });
}

export default function useBulkMarketingSubscribedConfirm({
    resetListSelect,
    setShouldClearWeb5Checkboxes,
}) {
    const giveConsent = useGiveConsent();
    const clearPayloadTimeoutRef = useRef(null);
    const giveConsentAbortRef = useRef(null);
    const [confirmState, setConfirmState] = useState({
        isOpen: false,
        isSubmitting: false,
        payload: null,
    });

    const schedulePayloadCleanup = useCallback(() => {
        clearTimeout(clearPayloadTimeoutRef.current);
        clearPayloadTimeoutRef.current = setTimeout(() => {
            setConfirmState((prev) => (prev.isOpen ? prev : { ...prev, payload: null }));
        }, BULK_MARKETING_SUBSCRIBE_CONFIRM_CLOSE_TIMEOUT);
    }, []);

    useEffect(() => {
        return () => {
            clearTimeout(clearPayloadTimeoutRef.current);
            giveConsentAbortRef.current?.abort();
        };
    }, []);

    const handleOpenBulkSubscribedConfirm = useCallback((payload) => {
        if (!payload?.entity || !payload?.filterKey) return;

        clearTimeout(clearPayloadTimeoutRef.current);

        setConfirmState({
            isOpen: true,
            isSubmitting: false,
            payload,
        });
    }, []);

    const handleCloseBulkSubscribedConfirm = useCallback(() => {
        setConfirmState((prev) => {
            if (prev.isSubmitting) return prev;

            schedulePayloadCleanup();
            return {
                ...prev,
                isOpen: false,
            };
        });
    }, [schedulePayloadCleanup]);

    const handleConfirmBulkSubscribed = useCallback(() => {
        const payload = confirmState.payload;
        if (!payload?.entity || !payload?.filterKey) return;

        giveConsentAbortRef.current?.abort();
        const abortController = new AbortController();
        giveConsentAbortRef.current = abortController;
        const { signal } = abortController;

        setConfirmState((prev) => ({ ...prev, isSubmitting: true }));

        giveConsent(payload.entity, payload.filterKey)
            .then(({ response, subscribedId }) => {
                if (!response) {
                    throw new Error('no_bulk_token');
                }
                const [token] = String(response).split(',');
                if (!token) {
                    throw new Error('no_bulk_token');
                }
                return pollBulkUntilComplete(token, { signal }).then((result) => ({
                    ...result,
                    subscribedId,
                }));
            })
            .then(({ success, subscribedId }) => {
                if (success) {
                    (payload.selectedIds || []).forEach((id) => {
                        patchMarketingStatusRow(payload.entity, id, subscribedId);
                    });
                }

                publish(`${REFRESH_TABLE}_${payload.filterKey}`);
                resetListSelect(payload.entity, true);
                setShouldClearWeb5Checkboxes(payload.entity, true);

                setConfirmState((prev) => ({
                    ...prev,
                    isOpen: false,
                }));

                if (success) {
                    successToast({
                        title: getLiteral(
                            'title_toast_marketing_campaigns_marketing_status_update_success',
                        ),
                        text: getLiteral(
                            'label_toast_marketing_campaigns_marketing_status_update_success_message',
                        ),
                    });
                } else {
                    showMarketingStatusUpdateError();
                }
                schedulePayloadCleanup();
            })
            .catch((err) => {
                if (err?.message === 'Aborted') return;
                showMarketingStatusUpdateError();
            })
            .finally(() => {
                setConfirmState((prev) => ({ ...prev, isSubmitting: false }));
            });
    }, [
        confirmState.payload,
        giveConsent,
        resetListSelect,
        schedulePayloadCleanup,
        setShouldClearWeb5Checkboxes,
    ]);

    return {
        confirmState,
        handleOpenBulkSubscribedConfirm,
        handleCloseBulkSubscribedConfirm,
        handleConfirmBulkSubscribed,
    };
}
